import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Plus, Edit2, Trash2, Search, Loader2, X, Banknote } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const Lenders = () => {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLender, setEditingLender] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'bank',
    products: 'personal',
    interest_rate: '',
    processing_fee: '',
    eligibility_criteria: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    fetchLenders();
  }, []);

  const fetchLenders = async () => {
    try {
      const response = await api.get('/lenders');
      setLenders(response.data);
    } catch (error) {
      showError('Failed to fetch lenders');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (lender = null) => {
    if (lender) {
      setEditingLender(lender);
      setFormData({
        ...lender,
        products: Array.isArray(lender.products) ? lender.products.join(',') : '',
      });
    } else {
      setEditingLender(null);
      setFormData({
        name: '',
        type: 'bank',
        products: 'personal',
        interest_rate: '',
        processing_fee: '',
        eligibility_criteria: '',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        products: formData.products.split(',').map((p) => p.trim()).filter(Boolean),
      };
      if (editingLender) {
        await api.put(`/lenders/${editingLender.id}`, data);
        showSuccess('Lender updated successfully');
      } else {
        await api.post('/lenders', data);
        showSuccess('Lender created successfully');
      }
      setShowModal(false);
      fetchLenders();
    } catch (error) {
      showError(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lender?')) return;
    try {
      await api.delete(`/lenders/${id}`);
      showSuccess('Lender deleted successfully');
      fetchLenders();
    } catch (error) {
      showError('Failed to delete lender');
    }
  };

  const filtered = lenders.filter((l) => l.name.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Lenders</h1>
            <p className="text-muted-foreground">Manage banks, NBFCs and lending partners</p>
          </div>
          <button
            data-testid="add-lender-button"
            onClick={() => handleOpenModal()}
            className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
          >
            <Plus size={20} />
            Add Lender
          </button>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              data-testid="search-lenders"
              placeholder="Search lenders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.length === 0 ? (
            <div className="col-span-3 text-center py-12 text-muted-foreground">No lenders found</div>
          ) : (
            filtered.map((lender) => (
              <div key={lender.id} data-testid={`lender-card-${lender.id}`} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Banknote className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">{lender.name}</h3>
                      <p className="text-xs text-muted-foreground capitalize">{lender.type}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  {lender.interest_rate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Interest:</span>
                      <span className="text-foreground font-medium">{lender.interest_rate}</span>
                    </div>
                  )}
                  {lender.processing_fee && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Processing Fee:</span>
                      <span className="text-foreground font-medium">{lender.processing_fee}</span>
                    </div>
                  )}
                  {lender.contact_person && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Contact:</span>
                      <span className="text-foreground font-medium">{lender.contact_person}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {lender.products && lender.products.map((p) => (
                    <span key={`${lender.id}-${p}`} className="px-2 py-1 rounded text-xs bg-muted text-foreground capitalize">{p}</span>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                  <button
                    data-testid={`edit-lender-${lender.id}`}
                    onClick={() => handleOpenModal(lender)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button
                    data-testid={`delete-lender-${lender.id}`}
                    onClick={() => handleDelete(lender.id)}
                    className="p-2 rounded-lg hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 size={16} className="text-destructive" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">{editingLender ? 'Edit Lender' : 'Add New Lender'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Type *</label>
                  <select required value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                    <option value="bank">Bank</option>
                    <option value="nbfc">NBFC</option>
                    <option value="fintech">Fintech</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Products (comma-separated) *</label>
                  <input type="text" required placeholder="personal, business, home" value={formData.products} onChange={(e) => setFormData({ ...formData, products: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Interest Rate</label>
                  <input type="text" placeholder="e.g., 10-15%" value={formData.interest_rate} onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Processing Fee</label>
                  <input type="text" placeholder="e.g., 1-2%" value={formData.processing_fee} onChange={(e) => setFormData({ ...formData, processing_fee: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Eligibility Criteria</label>
                  <textarea rows="3" value={formData.eligibility_criteria} onChange={(e) => setFormData({ ...formData, eligibility_criteria: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Contact Person</label>
                  <input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Contact Phone</label>
                  <input type="tel" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Contact Email</label>
                  <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2 flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-accent">Cancel</button>
                  <button type="submit" disabled={submitting} data-testid="submit-lender" className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="animate-spin" size={18} />}
                    {editingLender ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Lenders;
